import React, { useState, useEffect } from 'react';
import './feesConfigurationList.css';

const FeesConfigurationList = () => {
  // State for main configuration
  const [configurations, setConfigurations] = useState([]);
  const [shippingOptions, setShippingOptions] = useState(['C&F', 'CIF', 'FOB']);
  const [feeTypes, setFeeTypes] = useState([]);

  const [showDeleteConfigDialog, setShowDeleteConfigDialog] = useState(false);
  const [configToDelete, setConfigToDelete] = useState(null);

  // Form states
  const [selectedShipping, setSelectedShipping] = useState('');
  const [selectedFees, setSelectedFees] = useState([]);

  // New fee type state
  const [newFeeName, setNewFeeName] = useState('');
  const [isAddingFee, setIsAddingFee] = useState(false);

  // UI states
  const [activeView, setActiveView] = useState('config');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');

  // Edit states
  const [editingFeeId, setEditingFeeId] = useState(null);
  const [tempEditFeeName, setTempEditFeeName] = useState('');
  const [showSaveConfirmDialog, setShowSaveConfirmDialog] = useState(false);
  const [feeToEdit, setFeeToEdit] = useState(null);

  // Delete states
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [feeToDelete, setFeeToDelete] = useState(null);

  // Fetch initial data
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setLoading(true);

        // Fetch all port fee configurations
        const configResponse = await fetch('https://car.amar.co.jp/api/authorization/admin-shipment-panel/get-all-port-fee-config', {
          method: 'GET',
          headers: {
            'accept': 'application/json'
          }
        });
        const configData = await configResponse.json();

        const mappedConfigs = configData.map(config => ({
          id: config.portFeeConfigId,
          shippingOption: config.shippingOptionName,
          fees: config.feeConfigurationList
        }));
        setConfigurations(mappedConfigs);

        // Fetch all fee config types
        const feeTypesResponse = await fetch('https://car.amar.co.jp/api/authorization/admin-shipment-panel/get-all-fee-config-type', {
          method: 'GET',
          headers: {
            'accept': 'application/json'
          }
        });
        const feeTypesData = await feeTypesResponse.json();
        setFeeTypes(feeTypesData);

        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };

    fetchInitialData();
  }, []);

  // Auto-close notifications
  useEffect(() => {
    const timer = setTimeout(() => {
      if (successMessage) setSuccessMessage('');
      if (error) setError(null);
    }, 3000);
    return () => clearTimeout(timer);
  }, [successMessage, error]);

  // Handle shipping option change
  const handleShippingChange = (e) => {
    setSelectedShipping(e.target.value);
  };

  // Toggle fee selection
  const toggleFeeSelection = (fee) => {
    setSelectedFees(prev =>
      prev.includes(fee)
        ? prev.filter(f => f !== fee)
        : [...prev, fee]
    );
  };

  const confirmDeleteConfiguration = async () => {
    try {
      const response = await fetch(
        `https://car.amar.co.jp/api/authorization/admin-shipment-panel/delete-port-fee-config/${encodeURIComponent(configToDelete.fees[0])}/${encodeURIComponent(configToDelete.shippingOption)}`,
        {
          method: 'DELETE',
          headers: {
            'accept': 'application/json'
          }
        }
      );

      if (response.ok) {
        setSuccessMessage('Fee configuration deleted successfully');
        // Refresh the configurations
        const configResponse = await fetch('https://car.amar.co.jp/api/authorization/admin-shipment-panel/get-all-port-fee-config', {
          method: 'GET',
          headers: {
            'accept': 'application/json'
          }
        });
        const configData = await configResponse.json();

        const mappedConfigs = configData.map(config => ({
          id: config.portFeeConfigId,
          shippingOption: config.shippingOptionName,
          fees: config.feeConfigurationList
        }));
        setConfigurations(mappedConfigs);
      } else {
        const errorText = await response.text();
        setError(errorText || 'Failed to delete configuration');
      }
    } catch (err) {
      setError(err.message || 'Failed to delete configuration');
    } finally {
      setShowDeleteConfigDialog(false);
      setConfigToDelete(null);
    }
  };

  const cancelDeleteConfiguration = () => {
    setShowDeleteConfigDialog(false);
    setConfigToDelete(null);
  };

  // Reset form
  const resetForm = () => {
    setSelectedShipping('');
    setSelectedFees([]);
    setError(null);
  };

  // Delete configuration
  const deleteConfiguration = (config) => {
    setConfigToDelete(config);
    setShowDeleteConfigDialog(true);
  };


  // Delete fee type handlers
  const handleDeleteClick = (fee) => {
    setFeeToDelete(fee);
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    try {
      const feeTypeObj = feeTypes.find(f => f.feeConfigType === feeToDelete);

      if (!feeTypeObj) {
        setError('Fee type not found');
        return;
      }

      const response = await fetch(
        `https://car.amar.co.jp/api/authorization/admin-shipment-panel/fee-config/${feeTypeObj.feeConfigTypeId}`,
        {
          method: 'DELETE',
          headers: {
            'accept': 'application/json'
          }
        }
      );

      const responseText = await response.text();

      if (response.ok) {
        setSuccessMessage('Fee type configuration deleted successfully');
        const feeTypesResponse = await fetch('https://car.amar.co.jp/api/authorization/admin-shipment-panel/get-all-fee-config-type', {
          method: 'GET',
          headers: {
            'accept': 'application/json'
          }
        });
        const feeTypesData = await feeTypesResponse.json();
        setFeeTypes(feeTypesData);
      } else {
        if (responseText.includes('not found')) {
          setError('Fee config type not found');
        } else {
          setError(responseText || 'Failed to delete fee type');
        }
      }
    } catch (err) {
      setError(err.message || 'Failed to delete fee type');
    } finally {
      setShowDeleteDialog(false);
      setFeeToDelete(null);
    }
  };

  const cancelDelete = () => {
    setShowDeleteDialog(false);
    setFeeToDelete(null);
  };

  // Edit fee type handlers
  const handleEditClick = (feeObj) => {
    setEditingFeeId(feeObj.feeConfigTypeId);
    setTempEditFeeName(feeObj.feeConfigType);
  };

  const handleSaveClick = (feeObj) => {
    setFeeToEdit(feeObj);
    setShowSaveConfirmDialog(true);
  };

  const confirmEdit = async () => {
    if (!tempEditFeeName.trim()) {
      setError('Please enter a fee name');
      setShowSaveConfirmDialog(false);
      return;
    }

    try {
      const response = await fetch(
        `https://car.amar.co.jp/api/authorization/admin-shipment-panel/fee-config/${feeToEdit.feeConfigTypeId}?feeConfigType=${encodeURIComponent(tempEditFeeName)}`,
        {
          method: 'PUT',
          headers: {
            'accept': 'application/json'
          }
        }
      );

      const responseText = await response.text();

      if (response.ok) {
        setSuccessMessage('Fee type configuration updated successfully');
        const feeTypesResponse = await fetch('https://car.amar.co.jp/api/authorization/admin-shipment-panel/get-all-fee-config-type', {
          method: 'GET',
          headers: {
            'accept': 'application/json'
          }
        });
        const feeTypesData = await feeTypesResponse.json();
        setFeeTypes(feeTypesData);
      } else {
        if (responseText.includes('not found')) {
          setError('Fee config type not found');
        } else if (responseText.includes('already exists')) {
          setError('Fee config type already exists');
        } else {
          setError(responseText || 'Failed to update fee type');
        }
      }
    } catch (err) {
      setError(err.message || 'Failed to update fee type');
    } finally {
      setShowSaveConfirmDialog(false);
      setEditingFeeId(null);
      setTempEditFeeName('');
      setFeeToEdit(null);
    }
  };

  const cancelEdit = () => {
    setEditingFeeId(null);
    setTempEditFeeName('');
  };

  // Save configuration
  const saveConfiguration = async () => {
    if (!selectedShipping || selectedFees.length === 0) {
      setError('Please select shipping option and at least one fee');
      return;
    }

    const shippingOptionType = selectedShipping === 'C&F' ? 'CNF' : selectedShipping;

    try {
      const response = await fetch('https://car.amar.co.jp/api/authorization/admin-shipment-panel/port-fee-config', {
        method: 'POST',
        headers: {
          'accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          shippingOptionType,
          feeConfigTypes: selectedFees
        })
      });

      if (response.ok) {
        setSuccessMessage('Port fee configuration saved successfully');

        const configResponse = await fetch('https://car.amar.co.jp/api/authorization/admin-shipment-panel/get-all-port-fee-config', {
          method: 'GET',
          headers: {
            'accept': 'application/json'
          }
        });
        const configData = await configResponse.json();

        const mappedConfigs = configData.map(config => ({
          id: config.portFeeConfigId,
          shippingOption: config.shippingOptionName,
          fees: config.feeConfigurationList
        }));
        setConfigurations(mappedConfigs);

        resetForm();
      } else {
        const responseText = await response.text();
        let errorMessage = responseText;
        try {
          const errorData = JSON.parse(responseText);
          errorMessage = errorData.message || responseText;
        } catch (e) { }
        errorMessage = errorMessage.replace(/^Error saving port fee configuration:\s*/i, '');
        setError(errorMessage);
      }
    } catch (err) {
      setError(err.message || 'Failed to save configuration');
    }
  };

  // Add new fee type
  const addNewFeeType = async () => {
    if (!newFeeName.trim()) {
      setError('Please enter a fee name');
      return;
    }

    try {
      const response = await fetch(
        `https://car.amar.co.jp/api/authorization/admin-shipment-panel/fee-config?feeConfigType=${encodeURIComponent(newFeeName)}`,
        {
          method: 'POST',
          headers: {
            'accept': 'application/json'
          },
          body: ''
        }
      );

      const responseText = await response.text();

      if (response.ok) {
        setSuccessMessage('Fee type configuration saved successfully');

        const feeTypesResponse = await fetch('https://car.amar.co.jp/api/authorization/admin-shipment-panel/get-all-fee-config-type', {
          method: 'GET',
          headers: {
            'accept': 'application/json'
          }
        });
        const feeTypesData = await feeTypesResponse.json();
        setFeeTypes(feeTypesData);

        setNewFeeName('');
        setIsAddingFee(false);
      } else {
        if (responseText.includes('already exists')) {
          setError(`Fee config type already exists: ${newFeeName}`);
        } else {
          setError(responseText || 'Failed to save fee type');
        }
      }
    } catch (err) {
      setError(err.message || 'Failed to save fee type');
    }
  };

  // Render fee item in view or edit mode
  const renderFeeItem = (feeObj) => {
    if (editingFeeId === feeObj.feeConfigTypeId) {
      return (
        <div className="fee-type-item editing">
          <input
            type="text"
            value={tempEditFeeName}
            onChange={(e) => setTempEditFeeName(e.target.value)}
            className="fee-edit-input"
            placeholder="Enter fee name"
          />
          <div className="fee-item-actions">
            <button
              className="fee-action-btn save-btn-fee-config-new"
              onClick={() => handleSaveClick(feeObj)}
              title="Save"
            >
              <svg className="save-icon" viewBox="0 0 24 24">
                <path d="M15,9H5V5H15M12,19A3,3 0 0,1 9,16A3,3 0 0,1 12,13A3,3 0 0,1 15,16A3,3 0 0,1 12,19M17,3H5C3.89,3 3,3.9 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V7L17,3Z" />
              </svg>
            </button>
            <button
              className="fee-action-btn cancel-btn-fee-config-new"
              onClick={cancelEdit}
              title="Cancel"
            >
              <svg className="cancel-icon" viewBox="0 0 24 24">
                <path d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z" />
              </svg>
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="fee-type-item">
        <div className="fee-name">{feeObj.feeConfigType}</div>
        <div className="fee-item-actions">
          <button
            className="fee-action-btn edit-btn-fee-config-new"
            onClick={() => handleEditClick(feeObj)}
            title="Edit"
          >
            <svg className="edit-icon" viewBox="0 0 24 24">
              <path d="M20.71,7.04C21.1,6.65 21.1,6 20.71,5.63L18.37,3.29C18,2.9 17.35,2.9 16.96,3.29L15.12,5.12L18.87,8.87M3,17.25V21H6.75L17.81,9.93L14.06,6.18L3,17.25Z" />
            </svg>
          </button>
          <button
            className="fee-action-btn delete-btn-fee-config-new"
            onClick={() => handleDeleteClick(feeObj.feeConfigType)}
            title="Delete"
          >
            <svg className="delete-icon" viewBox="0 0 24 24">
              <path d="M19,4H15.5L14.5,3H9.5L8.5,4H5V6H19M6,19A2,2 0 0,0 8,21H16A2,2 0 0,0 18,19V7H6V19Z" />
            </svg>
          </button>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
        <p>Loading data...</p>
      </div>
    );
  }

  return (
    <div className="fee-config-app">
      <header className="app-header">
        <h1>Fee Configuration Manager</h1>
        <div className="view-switcher">
          <button
            className={activeView === 'config' ? 'active' : ''}
            onClick={() => setActiveView('config')}
          >
            Configurations
          </button>
          <button
            className={activeView === 'fees' ? 'active' : ''}
            onClick={() => setActiveView('fees')}
          >
            Fee Types
          </button>
        </div>
      </header>

      <main className="app-main">
        {activeView === 'config' ? (
          <>
            <section className="config-form">
              <h2>Add New Configuration</h2>
              <div className="form-grid">
                <div className="form-group">
                  <label>Shipping Option</label>
                  <select
                    value={selectedShipping}
                    onChange={handleShippingChange}
                    className="form-select"
                  >
                    <option value="">Select Option</option>
                    {shippingOptions.map(option => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="fee-selection">
                <h3>Select Fees</h3>
                <div className="fee-checkboxes-fee-config-new">
                  {feeTypes.map(feeObj => (
                    <label key={feeObj.feeConfigType} className="fee-checkbox-fee-config-new">
                      <input
                        type="checkbox"
                        checked={selectedFees.includes(feeObj.feeConfigType)}
                        onChange={() => toggleFeeSelection(feeObj.feeConfigType)}
                      />
                      <span className="checkmark-fee-config-new"></span>
                      {feeObj.feeConfigType}
                    </label>
                  ))}
                </div>
              </div>

              <div className="form-actions">
                <button className="cancel-btn-fee-config-new" onClick={resetForm}>
                  Cancel
                </button>
                <button className="save-btn-fee-config-new" onClick={saveConfiguration}>
                  Save Configuration
                </button>
              </div>
            </section>

            <section className="config-table">
              <h2>Existing Configurations</h2>
              {configurations.length === 0 ? (
                <div className="empty-state">
                  <p>No configurations found. Add one above.</p>
                </div>
              ) : (
                <div className="table-container-fee-config-new">
                  <table className="configurations-table">
                    <thead>
                      <tr>
                        <th>Shipping Option</th>
                        <th>Fees</th>
                      </tr>
                    </thead>
                    <tbody>
                      {configurations.map(config => (
                        <tr key={config.id}>
                          <td className="shipping-cell">
                            <div className="shipping-option">
                              <span className="shipping-icon">
                                <svg viewBox="0 0 24 24">
                                  <path d="M19.35,10.03C18.67,6.59 15.64,4 12,4C9.11,4 6.6,5.64 5.35,8.03C2.34,8.36 0,10.9 0,14A6,6 0 0,0 6,20H19A5,5 0 0,0 24,15C24,12.36 21.95,10.22 19.35,10.03Z" />
                                </svg>
                              </span>
                              {config.shippingOption}
                            </div>
                          </td>
                          <td>
                            <div className="fee-list">
                              {config.fees.map(fee => (
                                <div key={fee} className="fee-tag">
                                  <span>{fee}</span>
                                  <button
                                    className="delete-fee-btn"
                                    onClick={() => deleteConfiguration({
                                      id: config.id,
                                      shippingOption: config.shippingOption,
                                      fees: [fee] // Passing the single fee to delete
                                    })}
                                    title={`Delete ${fee}`}
                                  >
                                    <svg className="delete-icon" viewBox="0 0 24 24">
                                      <path d="M19,4H15.5L14.5,3H9.5L8.5,4H5V6H19M6,19A2,2 0 0,0 8,21H16A2,2 0 0,0 18,19V7H6V19Z" />
                                    </svg>
                                  </button>
                                </div>
                              ))}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          </>
        ) : (
          <>
            <section className="fee-types">
              <h2>Manage Fee Types</h2>
              <div className="fee-types-list">
                {feeTypes.map(feeObj => renderFeeItem(feeObj))}
              </div>

              {isAddingFee ? (
                <div className="add-fee-form">
                  <input
                    type="text"
                    value={newFeeName}
                    onChange={(e) => setNewFeeName(e.target.value)}
                    placeholder="Enter new fee name"
                    className="add-fee-input"
                  />
                  <div className="fee-form-actions">
                    <button
                      className="cancel-btn-fee-config-new"
                      onClick={() => {
                        setIsAddingFee(false);
                        setNewFeeName('');
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      className="save-btn-fee-config-new"
                      onClick={addNewFeeType}
                      disabled={!newFeeName.trim()}
                    >
                      Save Fee
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  className="add-fee-btn-fee-config-new"
                  onClick={() => setIsAddingFee(true)}
                >
                  + Add New Fee Type
                </button>
              )}
            </section>
          </>
        )}
      </main>

      <div className="notification-container">
        {successMessage && (
          <div className="notification success">
            {successMessage}
            <span className="notification-close" onClick={() => setSuccessMessage('')}>×</span>
          </div>
        )}
        {error && (
          <div className="notification error">
            {error}
            <span className="notification-close" onClick={() => setError(null)}>×</span>
          </div>
        )}
      </div>

      {showDeleteDialog && (
        <div className="dialog-overlay">
          <div className="confirmation-dialog">
            <h3>Are you sure you want to delete?</h3>
            <div className="dialog-buttons">
              <button className="cancel-btn-fee-config-new" onClick={cancelDelete}>
                Cancel
              </button>
              <button className="save-btn-fee-config-new" onClick={confirmDelete}>
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {showDeleteConfigDialog && (
        <div className="dialog-overlay">
          <div className="confirmation-dialog">
            <h3>Are you sure you want to delete this? This action will remove it from all associated products and cannot be undone.</h3>
            <div className="dialog-buttons">
              <button className="cancel-btn-fee-config-new" onClick={cancelDeleteConfiguration}>
                Cancel
              </button>
              <button className="save-btn-fee-config-new" onClick={confirmDeleteConfiguration}>
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {showSaveConfirmDialog && (
        <div className="dialog-overlay">
          <div className="confirmation-dialog">
            <h3>Are you sure want to save?</h3>
            <div className="dialog-buttons">
              <button className="cancel-btn-fee-config-new" onClick={() => setShowSaveConfirmDialog(false)}>
                Cancel
              </button>
              <button className="save-btn-fee-config-new" onClick={confirmEdit}>
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FeesConfigurationList;